import { useState } from 'react';
import { NJButton } from '@engie-group/fluid-design-system-react';
import { UMFormStyles } from '../../urbanmap/_components';
import { ConfirmDeleteDialog } from '../page-accueil/ConfirmDeleteDialog';
import { CardRow, EmptyState, FamilyGroup, FamilyLink, HomeHeader } from '../page-accueil/HomeCards';
import { ProjectCard, type CardAction } from '../page-accueil/ProjectCard';
import type { ProjectCardItem } from '../page-accueil/types';
import { CURRENT_USER, toCardItem, toFamilies } from './homeModel';
import { useDeleteProject, useDuplicateProject, useProjectFlags } from './hooks';
import { PLAN, UMR } from '../proposition-c/tokens';
import type { ProjectSummary } from './types';

interface Props {
  projects: ProjectSummary[] | undefined;
  loading: boolean;
  error: boolean;
  onCreate: () => void;
  onOpen: (id: string) => void;
  flash: (message: string) => void;
}

/**
 * Accueil Plan au format de la page d'accueil Nemo : rangées horizontales de cartes
 * (favoris, mes projets, entité, archives) avec familles de projets dupliqués.
 */
export function HomePage({ projects, loading, error, onCreate, onOpen, flash }: Props) {
  const [pendingDelete, setPendingDelete] = useState<ProjectCardItem | null>(null);
  const flags = useProjectFlags();
  const duplicate = useDuplicateProject();
  const remove = useDeleteProject();

  const items = (projects ?? []).map(toCardItem);
  const active = items.filter((p) => !p.archived);
  const archived = items.filter((p) => p.archived);
  const favorites = active.filter((p) => p.favorite);
  const mine = active.filter((p) => p.owner.name === CURRENT_USER);
  const entity = active.filter((p) => p.owner.name !== CURRENT_USER);
  const parentName = (p: ProjectCardItem) => items.find((x) => x.id === p.parentId)?.name;

  const toggleFavorite = (project: ProjectCardItem) => {
    const next = !project.favorite;
    flags.mutate(
      { id: project.id, flags: { favorite: next } },
      { onSuccess: () => flash(next ? `« ${project.name} » ajouté à vos favoris.` : `« ${project.name} » retiré de vos favoris.`) },
    );
  };

  const setArchived = (project: ProjectCardItem, archive: boolean) => {
    flags.mutate(
      { id: project.id, flags: { archived: archive } },
      {
        onSuccess: () => flash(archive
          ? `« ${project.name} » archivé — vous le retrouverez dans « Projets archivés ».`
          : `« ${project.name} » sorti de l’archive.`),
      },
    );
  };

  const handleAction = (project: ProjectCardItem) => (action: CardAction) => {
    switch (action) {
      case 'ouvrir':
        return onOpen(project.id);
      case 'hypotheses':
        return onOpen(project.id);
      case 'plans':
        return flash(`Plans de développement · ${project.name} (à venir)`);
      case 'dupliquer':
        return duplicate.mutate(project.id, {
          onSuccess: (copy) => flash(`« ${project.name} » dupliqué : « ${copy.name} » créé.`),
        });
      case 'archiver':
        return setArchived(project, true);
      case 'restaurer':
        return setArchived(project, false);
      case 'supprimer':
        return setPendingDelete(project);
      default:
        return undefined;
    }
  };

  const confirmDelete = (project: ProjectCardItem) => {
    remove.mutate(project.id, {
      onSuccess: () => {
        setPendingDelete(null);
        flash(`« ${project.name} » supprimé définitivement.`);
      },
    });
  };

  const card = (project: ProjectCardItem, section: string) => (
    <div key={project.id} data-comment-anchor={`${section}-project-card-${project.id}`}>
    <ProjectCard
      project={project}
      parentName={parentName(project)}
      onAction={handleAction(project)}
      onToggleFavorite={() => toggleFavorite(project)}
    />
    </div>
  );

  const families = (list: ProjectCardItem[], section: string) =>
    toFamilies(list).map((family) => (
      <FamilyGroup key={family.root.id} name={family.root.name} count={1 + family.copies.length}>
        {card(family.root, section)}
        {family.copies.map((copy) => (
          <div key={copy.id} style={{ display: 'flex', gap: 8 }}>
            <FamilyLink />
            {card(copy, section)}
          </div>
        ))}
      </FamilyGroup>
    ));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: UMR.white }}>
      <UMFormStyles />
      <HomeHeader />

      <main style={{ flex: 1, overflowY: 'auto', padding: '28px 32px 40px' }}>
        <div style={{ maxWidth: 1360, margin: '0 auto' }}>
          {loading && <EmptyState icon="hourglass_top" title="Chargement des projets…" />}
          {error && <EmptyState icon="error" title="Impossible de charger les projets">Réessayez dans quelques instants.</EmptyState>}

          {projects && projects.length === 0 && (
            <EmptyState icon="folder_off" title="Aucun projet">
              <span>Créez un premier projet : choisissez un périmètre, les bâtiments UrbanMap y seront copiés.</span>
              <NJButton label="Nouveau projet" icon="add" onClick={onCreate} />
            </EmptyState>
          )}

          {projects && projects.length > 0 && (
            <>
              <CardRow
                title={`Mes projets favoris (${favorites.length})`}
                hint="Cœur plein = projet en favori. Cliquez dessus pour le retirer de vos favoris."
              >
                {favorites.length > 0 ? favorites.map((project) => card(project, 'favorites')) : (
                  <div style={{ fontSize: 13, lineHeight: '20px', color: UMR.textSecondary, padding: '8px 0' }}>
                    Aucun projet en favori — cliquez sur le cœur d’une carte pour en ajouter un.
                  </div>
                )}
              </CardRow>

              <CardRow
                title={`Mes derniers projets créés (${mine.length})`}
                hint="Un projet dupliqué reste rattaché à son projet d’origine : ils forment une famille."
                action={<NJButton label="Nouveau projet" onClick={onCreate} />}
              >
                {families(mine, 'mine')}
              </CardRow>

              <CardRow title={`Les derniers projets modifiés dans mon entité (${entity.length})`}>
                {families(entity, 'entity')}
              </CardRow>

              {archived.length > 0 && (
                <CardRow
                  title={`Projets archivés (${archived.length})`}
                  hint="Un projet archivé reste consultable et peut être sorti de l’archive à tout moment."
                >
                  {archived.map((project) => card(project, 'archived'))}
                </CardRow>
              )}

              <a
                href="#"
                className="nj-link"
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, color: PLAN.accentStrong }}
                onClick={(event) => {
                  event.preventDefault();
                  flash('Ouverture de la liste complète des projets');
                }}
              >
                <span className="material-icons" aria-hidden="true">add</span>
                Voir tous les projets
              </a>
            </>
          )}
        </div>
      </main>

      {pendingDelete && (
        <ConfirmDeleteDialog
          projectName={pendingDelete.name}
          planCount={pendingDelete.planCount}
          sharedWith={pendingDelete.members.length}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => confirmDelete(pendingDelete)}
        />
      )}
    </div>
  );
}
